import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  StackDivider,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { EmailTemplate } from "features/Resources/EmailTemplate/models";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useUpdateEmailTemplate, useCreateEmailTemplate } from "features/Resources/EmailTemplate/hooks";
import { useState, useMemo } from "react";
import { PrimaryButton } from "components/Button";
import { dirtyValues } from "helpers/form";
import { toEmailTemplateFormData } from "features/Resources/EmailTemplate/helpers";
import ActionRichTextAreaField from "components/FormElements/Fields/TemplateRichTextAreaField";
import { cleanupEmailRecipients } from "features/Resources/Email/helpers";
import { Path } from "features/Path/models";
import BccOrCCButton from "features/Resources/Email/Components/BccOrCCButton";
import EmailRecipientsField from "features/Resources/Email/Components/Fields/EmailRecipientsField";
import TemplateResourceOptionMenu from "features/Resources/SharedTemplate/Components/TemplateResourceOptionMenu";
import { yupResolver } from "@hookform/resolvers/yup";
import { emailTemplateSchema, EmailTemplateSchema } from "features/Resources/EmailTemplate/schema";
import { v2pathPathById } from "helpers/url/pages/v2/pathPath";
import NameField from "features/Resources/Shared/Components/Fields/NameField";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import TagByResourceType from "components/Tag/TagByResourceType";
import ArchivedTag from "components/Tag/ArchivedTag";
import BasePathTag from "components/Tag/BasePathTag";
import SelectPathsMenuButton from "features/Path/Components/Button/SelectPathsMenuButton";
import AttributeColumnLabel from "features/Resources/Shared/Components/AttributeColumnLabel";
import SchedulingTriggerModalButton from "features/Resources/Shared/Components/Button/SchedulingTriggerModalButton";
import { usePathsFieldArray } from "features/Resources/Shared/hooks/usePathsFieldArray";
import { useCurrentCompanyAdminQuery } from "features/Company/hooks";
import ComposeBox, { useComposeBoxColors, BORDERLESS_TRIX_SX } from "features/Resources/Shared/Components/ComposeBox";

export default function EmailTemplateForm({ id, data }: { id: string; data: EmailTemplate }) {
  const toast = useToast();
  const navigate = useNavigate();
  const form = useForm<EmailTemplateSchema>({
    mode: "onChange",
    resolver: yupResolver(emailTemplateSchema),
    values: toEmailTemplateFormData(data),
    defaultValues: { paths: [] },
  });
  const { mutate: onUpdate } = useUpdateEmailTemplate(data?.id);
  const { mutate: onCreate } = useCreateEmailTemplate();
  const {
    handleSubmit,
    control,
    watch,
    register,
    formState: { dirtyFields, errors },
  } = form;

  // Paths field logic - using shared hook
  const { pathFields, removePath, addPathId, selectedPathIds, getPathData, returnPathId } = usePathsFieldArray(form);

  // Paths field: Selected paths components
  const selectedPathsComponents = useMemo(
    () =>
      (pathFields || []).map((field: any, index: number) => {
        const path = getPathData(field.id);
        return (
          <BasePathTag
            key={field.fieldId}
            pathId={path?.id}
            pathName={path?.name}
            icon={path?.icon}
            iconColor={path?.iconColor}
            removeable
            onRemove={() => removePath(index)}
          />
        );
      }),
    [pathFields, getPathData, removePath],
  );

  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);

  // Watch for existing recipients
  const hasCC = (watch("cc") || []).length > 0;
  const hasBCC = (watch("bcc") || []).length > 0;

  // Compose box colors for the body bg + hover (matches EmailForm).
  const { bodyBg: composeBodyBg, bodyHoverBg: composeBodyHoverBg } = useComposeBoxColors();
  const { data: company } = useCurrentCompanyAdminQuery();

  const onSubmit = (formData: Partial<EmailTemplateSchema>) => {
    // For new templates, use all form data. For updates, use only dirty fields.
    const submitData = id === "new" ? formData : dirtyValues(formData, dirtyFields);

    // Early return if updating with no changes
    if (id !== "new" && (!submitData || Object.keys(submitData).length === 0)) {
      return;
    }

    // Convert paths to pathIds if present
    if (submitData.paths) {
      submitData.pathIds = ((submitData?.paths || []) as Path[]).map((path) => path.id);
    }

    // Clean up email recipients
    cleanupEmailRecipients(submitData, "to");
    cleanupEmailRecipients(submitData, "cc");
    cleanupEmailRecipients(submitData, "bcc");

    if (id === "new") {
      onCreate(submitData, {
        onSuccess: (res: any) => {
          toast({ title: "Email template created.", status: "success" });
          if (returnPathId) {
            navigate(v2pathPathById(returnPathId));
          } else if (formData?.pathIds?.length) {
            navigate(v2pathPathById(formData.pathIds[0]));
          } else {
            navigate(PEOPLE_TEAM_ROUTES.emailTemplates.index);
          }
        },
      });
    } else {
      onUpdate(submitData, { onSuccess: () => toast({ title: "Email template updated.", status: "success" }) });
    }
  };

  return (
    <VStack spacing={4} align="stretch" width="100%">
      {/* Top row: full width */}
      <Flex w="100%" justify="space-between" alignItems="center">
        <TagByResourceType type="email_template" />
        <HStack>
          {data?.archivedAt && <ArchivedTag />}
          <PrimaryButton
            onClick={handleSubmit(onSubmit, (errors) => {
              toast({ title: "Please fix the errors before continuing.", status: "error", duration: 5000 });
              console.error("Form validation errors:", errors);
            })}
          >
            {id === "new" ? "Add" : "Save"}
          </PrimaryButton>
          {data && <TemplateResourceOptionMenu template={data} />}
        </HStack>
      </Flex>

      {/* Name above the attributes row. */}
      <NameField form={form} />

      {/* Template attributes — Send At + Path sit side-by-side at the left,
          framed top/bottom with a vertical divider between. */}
      <HStack
        divider={<StackDivider borderColor="gray.200" />}
        spacing={0}
        py={4}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="gray.200"
        align="stretch"
        justify="flex-start"
      >
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
          <AttributeColumnLabel>Send At</AttributeColumnLabel>
          <FormControl isInvalid={!!errors.deliverAtTrigger}>
            <SchedulingTriggerModalButton
              form={form}
              label="Send At"
              fieldName="deliverAtTrigger"
              timingOnly={id === "new"}
              allowWeekendsFieldName="allowWeekends"
            />
            <FormErrorMessage mt={0} fontSize="xs">
              {(errors.deliverAtTrigger as any)?.message}
            </FormErrorMessage>
          </FormControl>
        </VStack>
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
          <AttributeColumnLabel>Path</AttributeColumnLabel>
          <Flex gap="1" align="center" wrap="wrap">
            {selectedPathsComponents}
            <SelectPathsMenuButton selectedPathIds={selectedPathIds} addPathId={addPathId} />
          </Flex>
        </VStack>
      </HStack>

      <Flex align="start" width="100%">
        {/* Email composer — wrapped in the shared ComposeBox so it matches the
            real EmailForm. From + To + CC + BCC + Subject header rows over a
            Trix body with the personalization-token button in the toolbar. */}
        <VStack flex="1" spacing={0} align="stretch" minW={0} pb={6}>
          <ComposeBox>
            {company && (
              <ComposeBox.HeaderRow label="From">
                <Text fontSize="sm" color="content.primary" noOfLines={1}>
                  {company.customEmailDefaultFrom || `${company.name} Onboarding (via Camino) <onboarding@joincamino.com>`}
                </Text>
              </ComposeBox.HeaderRow>
            )}
            {company?.customEmailDefaultReplyTo && (
              <ComposeBox.HeaderRow label="Reply To">
                <Text fontSize="sm" color="content.primary" noOfLines={1}>
                  {company.customEmailDefaultReplyTo}
                </Text>
              </ComposeBox.HeaderRow>
            )}
            <ComposeBox.HeaderRow label="To">
              <FormControl isInvalid={!!errors.to}>
                <EmailRecipientsField
                  variant="inline"
                  label="To"
                  fieldName="to"
                  form={form}
                  includeRelationTarget
                  additionalButtons={
                    <>
                      {!hasCC && !showCC && <BccOrCCButton onClick={() => setShowCC(true)}>CC</BccOrCCButton>}
                      {!hasBCC && !showBCC && <BccOrCCButton onClick={() => setShowBCC(true)}>BCC</BccOrCCButton>}
                    </>
                  }
                />
                <FormErrorMessage mt={0} fontSize="xs">{(errors.to as any)?.message}</FormErrorMessage>
              </FormControl>
            </ComposeBox.HeaderRow>
            {(hasCC || showCC) && (
              <ComposeBox.HeaderRow label="CC">
                <EmailRecipientsField
                  variant="inline"
                  label="CC"
                  fieldName="cc"
                  form={form}
                  includeRelationTarget
                />
              </ComposeBox.HeaderRow>
            )}
            {(hasBCC || showBCC) && (
              <ComposeBox.HeaderRow label="BCC">
                <EmailRecipientsField
                  variant="inline"
                  label="BCC"
                  fieldName="bcc"
                  form={form}
                  includeRelationTarget
                />
              </ComposeBox.HeaderRow>
            )}
            <ComposeBox.HeaderRow bg={composeBodyBg}>
              <FormControl isInvalid={!!errors.subject}>
                <Input
                  {...register("subject")}
                  variant="unstyled"
                  placeholder="Subject"
                  fontSize="md"
                  fontWeight="bold"
                  color="content.primary"
                  _placeholder={{ color: "fg.subtle", fontWeight: "normal" }}
                />
                <FormErrorMessage mt={0} fontSize="xs">{errors.subject?.message}</FormErrorMessage>
              </FormControl>
            </ComposeBox.HeaderRow>
            <Box
              px={4}
              pt={3}
              pb={2}
              bg={composeBodyBg}
              transition="background-color 0.15s"
              sx={{
                ...BORDERLESS_TRIX_SX,
                "&:hover:not(:focus-within)": { bg: composeBodyHoverBg },
              }}
            >
              <ActionRichTextAreaField
                name="body"
                label="Body"
                control={control}
                actionType="email"
                alwaysShowToolbar
                transparent
                placeholder="Write the body…"
              />
            </Box>
          </ComposeBox>
        </VStack>
      </Flex>
    </VStack>
  );
}
